import { z } from "zod";
import { Frequency } from "../utils/date.js";

export enum AccountCacheKeys {
  AccountBilling = "billing",
}

export const ItemPriceTierSchema = z.object({
  unitAmountDecimal: z.string().nullable(),
  flatAmountDecimal: z.string().nullable(),
  upTo: z.number().nullable(),
});

export const DiscountSchema = z.object({
  name: z.string().nullable(),
  percentOff: z.number().nullable(),
  amountOff: z.number().nullable(),
});

export const BillingAddonSchema = z.object({
  itemType: z.string(),
  quantity: z.number(),
  tiers: z.array(ItemPriceTierSchema).nullable(),
  maxQuantity: z.number().nullable(),
});

/**
 * Amounts in the currency's smallest unit, as Stripe returns them.
 */
export const SubscriptionPreviewSchema = z.object({
  currency: z.string(),
  lines: z.array(
    z.object({
      description: z.string().nullable(),
      amount: z.number(),
      quantity: z.number().nullable(),
    }),
  ),
  discount: z.number(),
  tax: z.number(),
  total: z.number(),
});

export const PlanChangePreviewSchema = z.object({
  direction: z.enum(["upgrade", "downgrade"]),
  currency: z.string(),
  dueToday: z.number(),
  /** Unused time on the old plan worth more than the new charges, kept on the customer's balance. */
  creditToday: z.number(),
  lines: z.array(
    z.object({
      description: z.string().nullable(),
      amount: z.number(),
      quantity: z.number().nullable(),
    }),
  ),
  discount: z.number(),
  tax: z.number(),
  recurringTotal: z.number(),
  effectiveAt: z.string().nullable(),
});

export const PendingPlanChangeSchema = z.object({
  planSlug: z.string(),
  planName: z.string(),
  interval: z.enum(Frequency),
  effectiveAt: z.string(),
});

export const AccountContactInfoSchema = z.object({
  email: z
    .array(
      z.object({
        value: z.string().optional(),
        primary: z.boolean().optional(),
      }),
    )
    .optional(),
  /**
   * @deprecated Replaced with `dub`
   */
  rewardful: z
    .object({
      referralId: z.string(),
    })
    .optional(),
  dub: z
    .object({
      clickId: z.string(),
    })
    .optional(),
});

export type ItemPriceTier = z.infer<typeof ItemPriceTierSchema>;
export type Discount = z.infer<typeof DiscountSchema>;
export type BillingAddon = z.infer<typeof BillingAddonSchema>;
export type SubscriptionPreview = z.infer<typeof SubscriptionPreviewSchema>;
export type PlanChangePreview = z.infer<typeof PlanChangePreviewSchema>;
export type PendingPlanChange = z.infer<typeof PendingPlanChangeSchema>;
export type AccountContactInfo = z.infer<typeof AccountContactInfoSchema>;

export const PlanSlugSchema = z.enum(["free", "starter", "team"]);
export type PlanSlug = z.infer<typeof PlanSlugSchema>;

/**
 * No slug or an unknown one counts as starter; only an explicit free slug is capped.
 * TODO: needs a migration to backfill planSlug from the subscription plan before this can default to free.
 */
export function resolvePlanSlug(planSlug: string | null | undefined): PlanSlug {
  const parsed = PlanSlugSchema.safeParse(planSlug);
  return parsed.success ? parsed.data : "starter";
}

/** null means no limit. */
export type PlanLimits = {
  organizationLimit: number | null;
  factLimit: number | null;
  contactLimit: number | null;
  apiKeyLimit: number | null;
  /** Keys issued for the project's agent, counted apart from `apiKeyLimit` */
  agentKeyLimit: number | null;
  seatLimit: number | null;
  whatsapp: boolean;
};

/** Usage at or above this share of a limit is shown as a warning. */
export const usageWarningRatio = 0.8;

/** Facts and contacts count every row ever created, removing one does not free up room. */
export const planLimits: Record<PlanSlug, PlanLimits> = {
  free: {
    organizationLimit: 1,
    factLimit: 40,
    contactLimit: 150,
    apiKeyLimit: 1,
    agentKeyLimit: 1,
    seatLimit: 1,
    whatsapp: false,
  },
  starter: {
    organizationLimit: null,
    factLimit: null,
    contactLimit: null,
    apiKeyLimit: null,
    agentKeyLimit: null,
    seatLimit: null,
    whatsapp: true,
  },
  team: {
    organizationLimit: null,
    factLimit: null,
    contactLimit: null,
    apiKeyLimit: null,
    agentKeyLimit: null,
    seatLimit: null,
    whatsapp: true,
  },
};
